import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { User } from '../model/user';
import { captureSurfacedFailure, isExpectedAuthRejection } from '../util/sentry-capture';
import { failIfSwallowed } from '../util/swallowed-http';
import { extractErrorMessage } from './admin-error';
import { AuthService } from './auth.service';

/** 권한 없음(기본)과 판정 불가를 화면에서 구분하기 위해 `/forbidden`에 넘기는 사유. */
type ForbiddenReason = 'unavailable';

/** APIService는 timeout을 걸지 않는다. 무응답이면 가드가 값을 못 내 빈 화면이 된다. */
const LOOKUP_TIMEOUT_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivateChild {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkRole(childRoute, state);
  }

  /**
   * 리다이렉트는 `navigate()` 호출이 아니라 UrlTree 반환으로 한다.
   * `false` + 명령형 navigate 조합은 네비게이션을 두 개 만들어 서로 경쟁시키는데,
   * 이 가드가 고치려는 버그가 바로 그 경쟁하는 리다이렉트다. UrlTree를 돌려주면
   * 라우터가 현재 전환을 취소하고 정확히 하나의 새 전환만 시작한다.
   */
  checkRole(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.getCurrentUserFromServer().pipe(
      // 인터셉터가 502를 EMPTY로 삼켜(error-http-interceptor.ts) 소스가 값 없이 완료된다.
      timeout(LOOKUP_TIMEOUT_MS),
      failIfSwallowed('RoleGuard: user lookup swallowed by interceptor'),
      map((user: User) => {
        // RoleGuard가 걸린 라우트에 data.roles를 빠뜨리면 예전 코드는 TypeError로 죽었다.
        // 방어적으로 권한 없음 처리한다(fail-closed).
        const roles: string[] = childRoute.data?.roles ?? [];
        const role = user?.role;
        const isPermitted = !!role && roles.includes(role);
        // 로그인 화면으로 보내면 안 된다. 이미 인증된 사용자라 로그인 페이지가
        // 다시 `/`로 돌려보내고, admin 서브도메인에서 `/`는 `/admin`으로 가서
        // 무한 리다이렉트 루프가 된다.
        return isPermitted ? true : this.forbiddenUrl(undefined, state.url);
      }),
      catchError((error) => of(this.resolveLookupFailure(error, state))),
    );
  }

  /**
   * 조회가 실패하면 권한을 판정할 수 없다. 에러를 그대로 흘리면 라우터가
   * NavigationError로 전환을 중단해 초기 진입 시 빈 화면이 뜬다.
   *
   * 이때 로그인 화면으로 보내는 것도 답이 아니다. AuthGuard는 로컬 JWT 만료만
   * 보므로 토큰이 살아 있는 채로 조회만 실패할 수 있고, 그러면 로그인 페이지가
   * 인증된 사용자를 returnUrl로 되돌려 같은 루프에 다시 빠진다.
   */
  private resolveLookupFailure(error: unknown, state: RouterStateSnapshot): UrlTree {
    const status = error instanceof HttpErrorResponse ? error.status : undefined;

    // isAuthenticated()는 형식이 깨진 토큰에 대해 jwtHelper.decodeToken이 throw한다.
    // 그 throw는 catchError 셀렉터 **안에서** 일어나 아무도 잡지 못하고, 가드가 값을
    // 하나도 emit하지 않은 채 끝나 빈 화면이 된다 — 이 PR이 없애려는 증상 그대로다.
    let hasLiveToken = false;
    try {
      hasLiveToken = this.authService.isAuthenticated();
    } catch {
      hasLiveToken = false;
    }
    this.reportLookupFailure(error, state, status, hasLiveToken);

    // 403은 장애가 아니라 확정된 거부다. `unavailable`로 뭉뚱그리면 영영 성공하지 못할
    // "다시 시도" 버튼을 주게 되고, 인터셉터의 "권한이 없습니다" 팝업과도 모순된다.
    if (hasLiveToken) return this.forbiddenUrl(status === 403 ? undefined : 'unavailable', state.url);
    return this.router.createUrlTree(['/tabs/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  /**
   * 조회 실패 보고. 확정된 거부(401-만료·403)는 info 로만 남기고, 살아 있는
   * 토큰의 401·타임아웃·5xx 등 일시 장애는 warning 메시지로, 코드 버그만
   * error 로 올린다.
   */
  private reportLookupFailure(
    error: unknown,
    state: RouterStateSnapshot,
    status: number | undefined,
    hasLiveToken: boolean,
  ): void {
    if (isExpectedAuthRejection(status, hasLiveToken)) {
      Sentry.captureMessage(`RoleGuard user lookup rejected (${status}): ${extractErrorMessage(error, state.url)}`, {
        level: 'info',
        tags: { feature: 'role-guard', phase: 'user-lookup' },
        extra: { url: state.url, status },
      });
      return;
    }
    captureSurfacedFailure(error, { feature: 'role-guard', phase: 'user-lookup', extra: { url: state.url, status } });
  }

  /** `from`은 판정에 실패한 원래 경로. forbidden.page.ts `retry()`가 이걸로 가드를 다시 태운다. */
  private forbiddenUrl(reason?: ForbiddenReason, from?: string): UrlTree {
    const queryParams: { reason?: ForbiddenReason; from?: string } = {};
    if (reason) queryParams.reason = reason;
    if (from) queryParams.from = from;

    return this.router.createUrlTree(['/forbidden'], { queryParams });
  }
}
